import { LoggableError } from '@yandex-int/messenger.errors';

import { LogErrorOptions, SafeLogErrorOptions } from './log-error';
import { LogEventStringOptions } from './log-event';
import { ACTION, COUNTERS, FEATURE } from './logger-enums';

type LogMethod = (...args: unknown[]) => void;

export interface Logger {
    debug: LogMethod;
    log: LogMethod;
    info: LogMethod;
    warn(error: LoggableError<SafeLogErrorOptions>, options?: LogErrorOptions): void;
    warn(error: LoggableError, options?: SafeLogErrorOptions): void;
    warn(error: Error, options: SafeLogErrorOptions): void;
    warn(error: unknown, options: SafeLogErrorOptions): void;
    error(error: LoggableError<SafeLogErrorOptions>, options?: LogErrorOptions): void;
    error(error: LoggableError, options?: SafeLogErrorOptions): void;
    error(error: Error, options: SafeLogErrorOptions): void;
    error(error: unknown, options: SafeLogErrorOptions): void;
    catch(options: SafeLogErrorOptions): (error: unknown) => void;
    event(name: string, options?: LogEventStringOptions): void;
    featureEvent(event: {
        feature: FEATURE;
        action: ACTION;
        options?: LogEventStringOptions;
        isSuccess?: boolean;
        timeoutMS?: number;
        onFail?: () => void;
    }): void;
    startDelta(counterId: COUNTERS): void;
    endDelta(counterId: COUNTERS, vars?: object): void;
    startFeatureDelta(event: {
        feature: FEATURE;
        action: ACTION;
        id?: string;
        options?: LogEventStringOptions;
        timeoutMS?: number | null;
        onFail?: () => void;
    }): void;
    endFeatureDelta(event: {
        feature: FEATURE;
        action: ACTION;
        id?: string;
        isSuccess: boolean;
        options?: LogEventStringOptions;
    }): void;
    discardFeatureDelta(event: { feature: FEATURE; action: ACTION; id?: string }): void;
}

type loggerFactory = (block: string) => Logger;

class LoggerFacade {
    private loggerFactory: loggerFactory = () => ({
        /* eslint-disable @typescript-eslint/no-empty-function */
        log: () => {},
        error: () => {},
        info: () => {},
        warn: () => {},
        debug: () => {},
        catch: () => () => {},
        event: () => () => {},
        featureEvent: () => () => {},
        startDelta: () => () => {},
        endDelta: () => () => {},
        startFeatureDelta: () => () => {},
        endFeatureDelta: () => () => {},
        discardFeatureDelta: () => () => {},
        /* eslint-enable @typescript-eslint/no-empty-function */
    });

    public configurate(factory: loggerFactory) {
        this.loggerFactory = factory;
    }

    public create(block: string): Logger {
        return this.loggerFactory(block);
    }
}

const loggerFacade = new LoggerFacade();

/**
 * @param {Object} loggerFactory
 */
export function configureLogger(loggerFactory: loggerFactory) {
    loggerFacade.configurate(loggerFactory);
}

/**
 * @param {string} block
 * @returns {Object}
 */
export function createLogger(block: string) {
    return loggerFacade.create(block);
}
